import axios from "@helpers/axios/private.axios";

/**
 * The user's favorite futures pairs from the backend, newest first.
 * Works as an external store for `useSyncExternalStore`.
 */

const FAVORITES_URL = "/futures-pairs/favorites";

const listeners = new Set();
let favorites = [];
let loadedUserId = null;

const toKey = (slug) => String(slug || "").toUpperCase();

export const sameSlug = (left, right) => toKey(left) === toKey(right);

const favoriteUrl = (pair) => `/futures-pairs/${pair.id}/favorite`;

const setFavorites = (next) => {
  favorites = next;
  listeners.forEach((listener) => listener());
};

/** @returns {object[]} stable reference until favorites change */
export const getFavoritePairs = () => favorites;

/**
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribeFavoritePairs = (listener) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * Loads favorites once per user, a guest has none.
 * @param {string} [userId]
 */
export const loadFavoritePairs = async (userId) => {
  const nextUserId = userId || null;

  if (nextUserId === loadedUserId) return;

  loadedUserId = nextUserId;
  setFavorites([]);

  if (!nextUserId) return;

  try {
    const { data } = await axios.get(FAVORITES_URL);

    if (loadedUserId === nextUserId) setFavorites(data.data);
  } catch (error) {
    // The next mount tries again
    if (loadedUserId === nextUserId) loadedUserId = null;
  }
};

// Updates the list right away and restores it if the request fails
const updateOptimistically = async (next, request) => {
  const previous = favorites;

  setFavorites(next);

  try {
    await request();
  } catch (error) {
    setFavorites(previous);
    throw error;
  }
};

/** Removes the pair from favorites. */
export const removeFavoritePair = async (slug) => {
  const pair = favorites.find((item) => sameSlug(item.slug, slug));

  if (!pair) return;

  await updateOptimistically(
    favorites.filter((item) => item !== pair),
    () => axios.delete(favoriteUrl(pair)),
  );
};

/** Adds the pair to the top of favorites or removes it. */
export const toggleFavoritePair = async (pair) => {
  if (!pair?.id || !pair?.slug) return;

  if (favorites.some((item) => sameSlug(item.slug, pair.slug))) {
    await removeFavoritePair(pair.slug);
    return;
  }

  await updateOptimistically([pair, ...favorites], () =>
    axios.post(favoriteUrl(pair)),
  );
};
